import * as z from "zod/mini";
import { PaymentMethod, PaymentRegion } from "./paymentMethod";
import type { FormFieldConfig } from "../../../mixitone/src/components/Forms/FormFieldsDefinition";

/**
 * Configuration schema for Cash payment method
 * Cash doesn't require any configuration
 */
export const CashConfigSchema = z.object({
  amount: z.number(),
});

/**
 * Type for Cash payment method configuration
 */
export type CashConfig = z.infer<typeof CashConfigSchema>;

/**
 * Cash payment method implementation
 * For door sales and in-person cash payments
 */
export class Cash extends PaymentMethod<CashConfig> {
  id = "cash";
  name = "Cash / door sales";
  supportedRegions: PaymentRegion[] = ["global"];

  /**
   * Get the configuration schema for Cash payments
   */
  getConfigSchema() {
    return CashConfigSchema;
  }

  /**
   * Get the form field configurations for Cash payments
   */
  getConfigFields(): FormFieldConfig[] {
    return [
      {
        type: "number",
        name: "amount",
        label: "Amount",
        required: true,
        helpText: "The amount to be paid in cash",
        prefix: "$",
        min: 0,
        step: 0.01,
      },
    ];
  }

  /**
   * Get a short description of the Cash payment method
   */
  getDescription(): string {
    return "Pay with cash in person at the venue";
  }

  /**
   * Get instructions for how to make a cash payment
   * @param config The configuration (empty for cash)
   */
  getInstructions(_config: CashConfig): string {
    return `
Please bring exact cash to the venue.
Payment will be collected at the door before entry.
    `.trim();
  }

  /**
   * Validate that the configuration is valid
   * @param config The configuration to validate
   */
  validateConfig(config: CashConfig): boolean {
    const result = CashConfigSchema.safeParse(config);
    return result.success;
  }
}

/**
 * Create a new Cash payment method instance
 */
export const createCashPaymentMethod = (): PaymentMethod<CashConfig> => {
  return new Cash();
};
